import Text from '../../../../components/ui/text'
import { Label } from '../../../components/ui/label'
import { CMSLink, Reference } from '../../CMSLink'
import { RichTextUpload } from '../Upload'
import { Video } from '../Video'
import escapeHTML from 'escape-html'
import React, { Fragment } from 'react'

type Node = {
  type: string
  value?: {
    url: string
    alt: string
  }
  children?: Node[]
  url?: string
  [key: string]: unknown
  newTab?: boolean
}

export type CustomRenderers = {
  [key: string]: (args: { node: Node; Serialize: SerializeFunction; index }) => JSX.Element // eslint-disable-line
}

type SerializeFunction = React.FC<{
  content?: Node[]
  customRenderers?: CustomRenderers
}>

const isText = (value: any): boolean =>
  typeof value === 'object' && value !== null && typeof value.text === 'string'

export const Serialize: SerializeFunction = ({ content, customRenderers }) => {
  return (
    <Fragment>
      {content?.map((node, i) => {
        if (isText(node)) {
          let text = <span dangerouslySetInnerHTML={{ __html: escapeHTML(node.text) }} />

          if (node.bold) {
            text = <strong key={i}>{text}</strong>
          }

          if (node.code) {
            text = <code key={i}>{text}</code>
          }

          if (node.italic) {
            text = <em key={i}>{text}</em>
          }

          if (node.underline) {
            text = (
              <span style={{ textDecoration: 'none' }} key={i}>
                {text}
              </span>
            )
            // text = <Highlight key={i} {...node} />
          }

          if (node.strikethrough) {
            text = (
              <span style={{ textDecoration: 'line-through' }} key={i}>
                {text}
              </span>
            )
          }

          return <Fragment key={i}>{text}</Fragment>
        }

        if (!node) {
          return null
        }

        if (
          customRenderers &&
          customRenderers[node.type] &&
          typeof customRenderers[node.type] === 'function'
        ) {
          return customRenderers[node.type]({ node, Serialize, index: i })
        }

        switch (node.type) {
          case 'br':
            return <br key={i} />
          case 'h1':
            return (
              <Text key={i} variant="subHeading">
                <Serialize content={node.children} customRenderers={customRenderers} />
              </Text>
            )
          case 'h2':
            return (
              <Text key={i} variant="subHeading">
                <Serialize content={node.children} customRenderers={customRenderers} />
              </Text>
            )
          case 'h3':
            return (
              <Text key={i} variant="heading">
                <Serialize content={node.children} customRenderers={customRenderers} />
              </Text>
            )
          case 'h4':
            return (
              <Text key={i} variant="heading">
                <Serialize content={node.children} customRenderers={customRenderers} />
              </Text>
            )
          case 'h5':
            return (
              <h5 key={i}>
                <Serialize content={node.children} customRenderers={customRenderers} />
              </h5>
            )
          case 'h6':
            return (
              <h6 key={i}>
                <Serialize content={node.children} customRenderers={customRenderers} />
              </h6>
            )
          case 'quote':
            return (
              <blockquote key={i}>
                <Serialize content={node.children} customRenderers={customRenderers} />
              </blockquote>
            )
          case 'ul':
            return (
              <ul key={i}>
                <Serialize content={node.children} customRenderers={customRenderers} />
              </ul>
            )
          case 'ol':
            return (
              <ol key={i}>
                <Serialize content={node.children} customRenderers={customRenderers} />
              </ol>
            )
          case 'li':
            return (
              <div key={i} className="pl-8">
                <li className="text-left" style={{ listStyleType: 'square', color: 'inherit' }}>
                  <Serialize content={node.children} customRenderers={customRenderers} />
                </li>
              </div>
            )
          case 'link':
            return (
              <CMSLink
                key={i}
                type={node.linkType === 'internal' ? 'reference' : 'custom'}
                url={node.url}
                className={`border-b text-gray-600 border-gray-300 hover:border-telesoft`}
                reference={node.doc as Reference}
                newTab={node?.newTab}
              >
                <Serialize content={node.children} customRenderers={customRenderers} />
              </CMSLink>
            )

          case 'upload': {
            return <RichTextUpload key={i} node={node} />
          }

          case 'label':
            return (
              <Label key={i}>
                <Serialize content={node.children} customRenderers={customRenderers} />
              </Label>
            )

          case 'large-body': {
            return (
              <Label key={i}>
                <Serialize content={node.children} customRenderers={customRenderers} />
              </Label>
            )
          }

          case 'video': {
            const { source, id: videoID } = node

            if (source === 'vimeo' || source === 'youtube') {
              return <Video key={i} platform={source} id={videoID as string} />
            }

            return null
          }

          default:
            return (
              <p className="" key={i}>
                <Serialize content={node.children} customRenderers={customRenderers} />
              </p>
            )
        }
      })}
    </Fragment>
  )
}
